import { getWorkflow } from '@jira/project-settings__workflow-services-document/src/getApiPrefix.tsx';
import type { ApiGetWorkflowResponseWithUsages } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	prefetchApiReadyMark,
	prefetchApiStartMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import { createResourceError } from '@atlassian/jira-router-resources-utils/src/utils/create-resource-error/index.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
import { consumeDependency } from '@atlassian/jira-router-resources-utils/src/utils/consume-dependency/index.tsx';
import type { ProjectWorkflowContext } from '@atlassian/jira-router-resources-project-workflow-context/src/services/project-workflow-context/types.tsx';
import { RESOURCE_TYPE_PROJECT_WORKFLOW_CONTEXT } from '@atlassian/jira-router-resources-project-workflow-context/src/services/project-workflow-context/constants.tsx';

export const fetchWorkflowDocument = async (
	routerContext: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
	isProjectScoped: boolean,
): Promise<ApiGetWorkflowResponseWithUsages> => {
	const { isAnonymous, cloudId } = tenantContext;
	const { match, dependencies } = routerContext;

	if (isAnonymous) {
		throw new Error("Workflows can't be edited in anonymous mode.");
	}
	if (!match.params.workflowId && !match.params.projectKey && !match.params.issueTypeId) {
		throw new Error('Workflow ID, project Key, or issue type ID is not defined.');
	}

	try {
		setMark(prefetchApiStartMark);

		let workflowDocument: ApiGetWorkflowResponseWithUsages | null = null;
		if (match.params.workflowId) {
			workflowDocument = await getWorkflow(
				cloudId,
				{
					workflowId: match.params.workflowId,
				},
				{ checkForDrafts: !isProjectScoped },
			);
		} else if (match.params.projectKey && match.params.issueTypeId) {
			const consumedDependency = await consumeDependency<ProjectWorkflowContext | null>(
				dependencies,
				RESOURCE_TYPE_PROJECT_WORKFLOW_CONTEXT,
			);
			if (consumedDependency) {
				workflowDocument = await getWorkflow(
					cloudId,
					{
						projectAndIssueType: {
							projectId: String(consumedDependency.id),
							issueTypeId: match.params.issueTypeId,
						},
					},
					{ checkForDrafts: !isProjectScoped },
				);
			} else {
				throw new Error('Project workflow context is not defined.');
			}
		}

		if (!workflowDocument) {
			throw new Error('Workflow ID, project ID, or issue type ID is not defined.');
		}

		setMark(prefetchApiReadyMark);
		return workflowDocument;
	} catch (error: unknown) {
		throw createResourceError(
			{
				component: 'spa.view.routes.admin-pages.workflows.workflow-document.getData',
				message: 'An error was thrown while prefetching workflow document data',
				name: 'ResourceError',
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
			error as any,
		);
	}
};

export const fetchWorkflowDocumentOld = async (
	{ match }: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
	isProjectScoped: boolean,
): Promise<ApiGetWorkflowResponseWithUsages> => {
	const { isAnonymous, cloudId } = tenantContext;

	if (isAnonymous || !match.params.workflowId) {
		const message = !match.params.workflowId
			? 'Workflow ID is not defined.'
			: "Workflows can't be edited in anonymous mode.";
		throw new Error(message);
	}

	try {
		setMark(prefetchApiStartMark);
		const workflowDocument = await getWorkflow(
			cloudId,
			{
				workflowId: match.params.workflowId,
			},
			{ checkForDrafts: !isProjectScoped },
		);
		setMark(prefetchApiReadyMark);
		return workflowDocument;
	} catch (error: unknown) {
		throw createResourceError(
			{
				component: 'spa.view.routes.admin-pages.workflows.workflow-document.getData',
				message: 'An error was thrown while prefetching workflow document data',
				name: 'ResourceError',
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
			error as any,
		);
	}
};
